import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, ThumbsUp, Users } from 'lucide-react';

const CommunityPage = () => {
  const { posts, experts } = useData();
  const navigate = useNavigate();

  const spaces = [
    { id: 'space-1', name: 'Battery Storage', icon: '🔋', members: 3120, bg: 'hsl(var(--accent-soft))' },
    { id: 'space-2', name: 'Grid Digitisation', icon: '📡', members: 1840, bg: 'hsl(var(--blue-soft))' },
    { id: 'space-3', name: 'Mini-grids · Africa', icon: '🌍', members: 2260, bg: 'hsl(var(--primary-soft))' },
    { id: 'space-4', name: 'Policy & Regulation', icon: '⚖️', members: 960, bg: 'hsl(var(--violet-soft))' },
    { id: 'space-5', name: 'Investor Lounge', icon: '💰', members: 410, bg: 'hsl(var(--accent-soft))' }
  ];

  const [activeSpace, setActiveSpace] = React.useState('space-1');

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <div className="mb-8">
          <span className="eyebrow">Community · "Connect"</span>
          <h2 className="mt-3 mb-2">Where knowledge becomes deployment</h2>
          <p style={{ color: 'hsl(var(--ink-muted))' }}>Topic spaces, expert Q&A, and a professional network of CoE-trained practitioners.</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          {/* Spaces Sidebar */}
          <Card className="p-4 h-fit">
            <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'hsl(var(--ink-2))' }}>Spaces</h4>
            <div className="space-y-1">
              {spaces.map(space => (
                <button
                  key={space.id}
                  onClick={() => setActiveSpace(space.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left ${activeSpace === space.id ? '' : ''}`}
                  style={{
                    backgroundColor: activeSpace === space.id ? 'hsl(var(--primary-soft))' : 'transparent'
                  }}
                >
                  <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: space.bg }}>
                    <span>{space.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{space.name}</div>
                    <div className="text-xs" style={{ color: 'hsl(var(--ink-faint))' }}>{space.members.toLocaleString()} members</div>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Feed */}
          <div className="md:col-span-2 space-y-4">
            {posts.map(post => {
              const author = experts.find(e => e.id === post.authorId);
              return (
                <Card key={post.id} className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold" style={{ backgroundColor: author?.avatarColor || 'hsl(var(--blue))', color: 'white' }}>
                      {author?.initials || 'U'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{author?.name || 'User'}</div>
                      <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>
                        {author?.role} · {author?.organization} · {new Date(post.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {post.attachedResourceId && (
                    <div className="text-xs mb-2 px-2 py-1 rounded inline-block" style={{ backgroundColor: 'hsl(var(--surface-2))', color: 'hsl(var(--ink-muted))' }}>
                      ↳ on "Model Tender Document"
                    </div>
                  )}

                  <p className="mb-3">{post.body}</p>

                  {post.replies && post.replies.length > 0 && post.replies[0].isAIDraft && (
                    <div className="p-4 rounded-lg mb-3" style={{ backgroundColor: 'hsl(var(--primary-tint))', borderLeft: '3px solid hsl(var(--primary))' }}>
                      <div className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--primary))' }}>✦ Grid Guide · draft answer (pending expert review)</div>
                      <p className="text-sm">{post.replies[0].body}</p>
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>
                    <button className="flex items-center gap-1 hover:text-primary">
                      <MessageSquare className="h-4 w-4" />
                      {post.replies?.length || 0} replies
                    </button>
                    <button className="flex items-center gap-1 hover:text-primary">
                      <ThumbsUp className="h-4 w-4" />
                      {Math.floor(Math.random() * 50) + 10}
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Right Rail */}
          <div className="space-y-6">
            <Card className="p-5">
              <h4 className="text-sm font-semibold mb-3">Featured Expert</h4>
              {experts[0] && (
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-lg font-semibold" style={{ backgroundColor: experts[0].avatarColor, color: 'white' }}>
                    {experts[0].initials}
                  </div>
                  <h4 className="mb-1">{experts[0].name}</h4>
                  <p className="text-xs mb-2" style={{ color: 'hsl(var(--ink-muted))' }}>
                    {experts[0].role} · {experts[0].organization}
                    {experts[0].isCoEAlumni && ` · CoE Alumna '${experts[0].coEYear}`}
                  </p>
                  <div className="flex flex-wrap gap-1 justify-center mb-3">
                    {experts[0].credentials.map(cred => (
                      <span key={cred} className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'hsl(var(--primary-soft))', color: 'hsl(var(--primary-700))' }}>
                        ✓ {cred}
                      </span>
                    ))}
                  </div>
                  <Button size="sm" className="w-full">Request a connect</Button>
                </div>
              )}
            </Card>

            <Card className="p-5">
              <h4 className="text-sm font-semibold mb-3">✦ Experts matched to your pathway</h4>
              <div className="space-y-3">
                {experts.slice(0, 3).map(expert => (
                  <div key={expert.id} className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: expert.avatarColor, color: 'white' }}>
                      {expert.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{expert.name}</div>
                      <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>98% match</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <ConciergeLauncher />
    </div>
  );
};

export default CommunityPage;
